import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BookOpen, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";

const RGB = "var(--glow-learn, var(--glow-home))";
export const ROUTE_MODULE = [["home/insurance", "insurance"], ["utilities", "utilities"], ["housing", "housing"], ["home-care", "home-care"], ["lease", "lease"],
  ["prep", "prep"], ["projects", "projects"], ["money/tax", "tax"], ["money/irregular", "irregular"], ["roommates", "roommates"], ["care", "care"]];

export function Body({ text }) {
  return text.split(/\n\s*\n/).map((blk, i) => {
    const lines = blk.split("\n").filter(Boolean);
    return lines.every((l) => /^\s*[-*] /.test(l))
      ? <ul key={i} className="list-disc space-y-1 pl-5">{lines.map((l, j) => <li key={j}>{l.replace(/^\s*[-*] /, "").replace(/\*\*/g, "")}</li>)}</ul>
      : <p key={i}>{blk.replace(/\*\*/g, "")}</p>;
  });
}

const cache = {};
export function LearnChip() {
  const { hub } = useHub() || {};
  const loc = useLocation();
  const [list, setList] = useState([]);
  const rest = hub ? loc.pathname.replace(`/h/${hub.id}/`, "") : "";
  const mod = (ROUTE_MODULE.find(([p]) => rest === p || rest.startsWith(`${p}/`)) || [])[1];
  useEffect(() => {
    if (!mod) return setList([]);
    if (cache[mod]) return setList(cache[mod]);
    api.get("/explainers", { params: { module: mod } }).then(({ data }) => { cache[mod] = data.explainers; setList(data.explainers); }).catch(() => setList([]));
  }, [mod]);
  if (!mod || !list.length) return null;
  return (
    <div className="mb-4 flex flex-wrap gap-2" data-testid={`learn-chips-${mod}`}>
      {list.map((e) => <Link key={e.id} to={`/h/${hub.id}/library/${e.id}`} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-q-line bg-q-raised px-4 text-sm font-semibold text-q-text" data-testid={`learn-chip-${e.id}`}><BookOpen className="h-4 w-4" /> Learn: {e.title}</Link>)}
    </div>
  );
}

function Article({ hub, eid }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/explainers/${eid}`).then(({ data }) => setA(data)).catch((e) => setErr(errMsg(e))); }, [eid]);
  if (err) return <p className="q-card p-6 text-q-muted" data-testid="learn-missing">{err}</p>;
  if (!a) return <Loader />;
  const vote = (helpful) => api.post(`/explainers/${eid}/feedback`, { helpful }).then(({ data }) => { setA(data); toast.success("Thanks for the feedback"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <article className="min-w-0 max-w-2xl space-y-4" data-testid="learn-article">
      <PageHeader eyebrow={`Learn · ${a.module_label}`} title={a.title} rgb={RGB} testid="learn-article-title" />
      <Link to={`/h/${hub.id}/library`} className="text-sm text-q-accent" data-testid="learn-back">Learn library</Link>
      <p className="text-base font-semibold text-q-text" data-testid="learn-summary">{a.summary}</p>
      <div className="space-y-3 text-sm leading-relaxed text-q-text sm:text-base" data-testid="learn-body"><Body text={a.body} /></div>
      {a.key_terms.length > 0 && <section className="q-card space-y-2 p-4" data-testid="learn-key-terms"><p className="q-label">Key terms</p>
        <dl className="space-y-2 text-sm">{a.key_terms.map((k) => <div key={k.term}><dt className="font-bold text-q-text">{k.term}</dt><dd className="text-q-muted">{k.meaning}</dd></div>)}</dl></section>}
      <div className="flex flex-wrap items-center gap-2" data-testid="learn-feedback"><span className="text-sm text-q-muted">Was this helpful?</span>
        <button className={`q-btn min-h-[44px] ${a.my_vote === "helpful" ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => vote(true)} data-testid="learn-helpful"><ThumbsUp className="h-4 w-4" /> Helpful</button>
        <button className={`q-btn min-h-[44px] ${a.my_vote === "not_helpful" ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => vote(false)} data-testid="learn-not-helpful"><ThumbsDown className="h-4 w-4" /> Not helpful</button></div>
    </article>
  );
}

export default function Library() {
  const { hub } = useHub();
  const { eid } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { if (!eid) api.get("/explainers").then(({ data }) => setD(data)).catch(() => setD({ explainers: [] })); }, [eid]);
  if (eid) return <Article key={eid} hub={hub} eid={eid} />;
  if (!d) return <Loader />;
  return (
    <div className="min-w-0 space-y-5" data-testid="learn-library">
      <PageHeader eyebrow="Home" title="Learn" rgb={RGB} testid="learn-title" />
      <p className="text-sm text-q-muted">Short guides to running a home. The same for every family.</p>
      {!d.explainers.length && <p className="text-sm text-q-faint" data-testid="learn-empty">Nothing published yet.</p>}
      <div className="grid gap-3 sm:grid-cols-2">{d.explainers.map((e) => (
        <Link key={e.id} to={`/h/${hub.id}/library/${e.id}`} className="q-card block min-w-0 space-y-1 p-4" data-testid={`learn-item-${e.id}`}>
          <p className="q-label">{e.module_label}</p><p className="font-display text-lg font-extrabold text-q-text">{e.title}</p><p className="text-sm text-q-muted">{e.summary}</p></Link>))}</div>
    </div>
  );
}
